import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, ComponentProps, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { VerticalTimeLine, VerticalTimeLineEntry } from 'record-it-components/dateTime/VerticalTimeLine/VerticalTimeLine';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { WorkTimeEntryBreak } from 'record-it-time-common/entity/entities/WorkTimeEntry/WorkTimeEntryDto';
import styles from './TrackedWorkTimeLine.module.css';
export type TrackedWorkTimeLineProps = PropsWithoutChildren<ComponentProps<'div'>> & {
  startDate: string;
  endDate: string;
  workTimeEntryBreaks: Array<WorkTimeEntryBreak>;
};
export const TrackedWorkTimeLine: FunctionComponent<TrackedWorkTimeLineProps> = React.memo(props => {
  const $ = _c(59);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let endDate;
  let remainingProps;
  let startDate;
  let workTimeEntryBreaks;
  if ($[2] !== t0) {
    ({
      workTimeEntryBreaks,
      startDate,
      endDate,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = endDate;
    $[4] = remainingProps;
    $[5] = startDate;
    $[6] = workTimeEntryBreaks;
  } else {
    endDate = $[3];
    remainingProps = $[4];
    startDate = $[5];
    workTimeEntryBreaks = $[6];
  }
  const {
    t
  } = useTranslation();
  let t1;
  if ($[7] !== workTimeEntryBreaks) {
    t1 = [...workTimeEntryBreaks].sort(_temp);
    $[7] = workTimeEntryBreaks;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const sortedBreaks = t1;
  const renderEntryFactory = _temp2;
  let entries;
  if ($[9] !== sortedBreaks) {
    let t2;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = workBreak => ({
        key: workBreak.index,
        startDate: new Date(workBreak.startDate),
        endDate: new Date(workBreak.endDate),
        renderContent: renderEntryFactory("break")
      });
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    const breakEntries = sortedBreaks.map(t2);
    entries = [];
    for (const breakEntry of breakEntries) {
      const lastEntry = entries[entries.length - 1];
      if (lastEntry && lastEntry.endDate.getTime() < breakEntry.startDate.getTime()) {
        entries.push({
          key: `${lastEntry.key}-${breakEntry.key}`,
          startDate: new Date(lastEntry.endDate),
          endDate: new Date(breakEntry.startDate),
          renderContent: renderEntryFactory("workTime")
        });
      }
      entries.push(breakEntry);
    }
    $[9] = sortedBreaks;
    $[10] = entries;
  } else {
    entries = $[10];
  }
  const breakEntriesWithFillers = entries;
  let t2;
  bb0: {
    const firstBreakEntry = breakEntriesWithFillers[0];
    const lastBreakEntry = breakEntriesWithFillers[breakEntriesWithFillers.length - 1];
    if (firstBreakEntry && lastBreakEntry) {
      let t3;
      if ($[12] !== startDate) {
        t3 = new Date(startDate);
        $[12] = startDate;
        $[13] = t3;
      } else {
        t3 = $[13];
      }
      let t4;
      if ($[14] !== firstBreakEntry.startDate) {
        t4 = new Date(firstBreakEntry.startDate);
        $[14] = firstBreakEntry.startDate;
        $[15] = t4;
      } else {
        t4 = $[15];
      }
      let t5;
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = renderEntryFactory("workTime");
        $[16] = t5;
      } else {
        t5 = $[16];
      }
      let t6;
      if ($[17] !== t3 || $[18] !== t4) {
        t6 = {
          key: "startDateToFirstBreakFiller",
          startDate: t3,
          endDate: t4,
          renderContent: t5
        };
        $[17] = t3;
        $[18] = t4;
        $[19] = t6;
      } else {
        t6 = $[19];
      }
      let t7;
      if ($[20] !== lastBreakEntry.endDate) {
        t7 = new Date(lastBreakEntry.endDate);
        $[20] = lastBreakEntry.endDate;
        $[21] = t7;
      } else {
        t7 = $[21];
      }
      let t8;
      if ($[22] !== endDate) {
        t8 = new Date(endDate);
        $[22] = endDate;
        $[23] = t8;
      } else {
        t8 = $[23];
      }
      let t9;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = renderEntryFactory("workTime");
        $[24] = t9;
      } else {
        t9 = $[24];
      }
      let t10;
      if ($[25] !== t7 || $[26] !== t8) {
        t10 = {
          key: "lastBreakToEndDateFiller",
          startDate: t7,
          endDate: t8,
          renderContent: t9
        };
        $[25] = t7;
        $[26] = t8;
        $[27] = t10;
      } else {
        t10 = $[27];
      }
      let t11;
      if ($[28] !== breakEntriesWithFillers || $[29] !== t10 || $[30] !== t6) {
        t11 = [t6, ...breakEntriesWithFillers, t10];
        $[28] = breakEntriesWithFillers;
        $[29] = t10;
        $[30] = t6;
        $[31] = t11;
      } else {
        t11 = $[31];
      }
      t2 = t11;
      break bb0;
    }
    let t3;
    if ($[32] !== startDate) {
      t3 = new Date(startDate);
      $[32] = startDate;
      $[33] = t3;
    } else {
      t3 = $[33];
    }
    let t4;
    if ($[34] !== endDate) {
      t4 = new Date(endDate);
      $[34] = endDate;
      $[35] = t4;
    } else {
      t4 = $[35];
    }
    let t5;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = renderEntryFactory("workTime");
      $[36] = t5;
    } else {
      t5 = $[36];
    }
    let t6;
    if ($[37] !== t3 || $[38] !== t4) {
      t6 = [{
        key: "noBreaksFallback",
        startDate: t3,
        endDate: t4,
        renderContent: t5
      }];
      $[37] = t3;
      $[38] = t4;
      $[39] = t6;
    } else {
      t6 = $[39];
    }
    t2 = t6;
  }
  const entries_0 = t2;
  let t3;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className={styles.legendColorPreview} data-entry-type="workTime" />;
    $[40] = t3;
  } else {
    t3 = $[40];
  }
  let t4;
  if ($[41] !== t) {
    t4 = t("components.timeTracking.TrackedWorkTimeLine.legend.workTime");
    $[41] = t;
    $[42] = t4;
  } else {
    t4 = $[42];
  }
  let t5;
  if ($[43] !== t4) {
    t5 = <span className={styles.legendColorText}>{t4}</span>;
    $[43] = t4;
    $[44] = t5;
  } else {
    t5 = $[44];
  }
  let t6;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className={styles.legendColorPreview} data-entry-type="break" />;
    $[45] = t6;
  } else {
    t6 = $[45];
  }
  let t7;
  if ($[46] !== t) {
    t7 = t("components.timeTracking.TrackedWorkTimeLine.legend.break");
    $[46] = t;
    $[47] = t7;
  } else {
    t7 = $[47];
  }
  let t8;
  if ($[48] !== t7) {
    t8 = <span className={styles.legendColorText}>{t7}</span>;
    $[48] = t7;
    $[49] = t8;
  } else {
    t8 = $[49];
  }
  let t9;
  if ($[50] !== t5 || $[51] !== t8) {
    t9 = <div className={styles.legend}>{t3}{t5}{t6}{t8}</div>;
    $[50] = t5;
    $[51] = t8;
    $[52] = t9;
  } else {
    t9 = $[52];
  }
  let t10;
  if ($[53] !== entries_0) {
    t10 = <VerticalTimeLine entries={entries_0} data-testid="TrackedWorkTimeLine.VerticalTimeLine" />;
    $[53] = entries_0;
    $[54] = t10;
  } else {
    t10 = $[54];
  }
  let t11;
  if ($[55] !== remainingProps || $[56] !== t10 || $[57] !== t9) {
    t11 = <div {...remainingProps}>{t9}{t10}</div>;
    $[55] = remainingProps;
    $[56] = t10;
    $[57] = t9;
    $[58] = t11;
  } else {
    t11 = $[58];
  }
  return t11;
});
function _temp(a, b) {
  return a.startDate.localeCompare(b.startDate);
}
function _temp2(type) {
  return () => <div className={styles.entry} data-entry-type={type} />;
}